import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, trackPageview } from "../lib/api";
import { Footer } from "../components/Footer";
import { LanguageToggle } from "../components/LanguageToggle";
import { useReactiveLang } from "../lib/i18n";
import { ShareButton } from "../components/ShareButton";
import { ISLANDS, ISLAND_BY_SLUG } from "../lib/constants";
import { LangContext, useT } from "../lib/LangContext";

// Dedicated Obituaries hub — pulls articles with `is_obituary=True` from the
// backend, filterable by island, sorted most-recent first. Obituaries are
// excluded from the general news feed so they don't appear scattered between
// unrelated stories. All UI text goes through useT().
//
// Language: this page is mounted at App-root and lives outside per-page
// LangContext.Providers, so we source `lang` from localStorage (same pattern
// as FloatingSubscribeCTA) and wrap the tree in our own provider.
const LANG_KEY = "ir_lang";
const SUPPORTED_LANGS = ["en", "es", "nl", "pap", "pap_aw"];
const readLang = () => {
  try {
    // Honor ?lang=xx URL query parameter first (highest priority) — matches
    // HomePage's behavior so shareable deep-links work in every language.
    const params = new URLSearchParams(window.location.search);
    const q = (params.get("lang") || "").toLowerCase();
    if (SUPPORTED_LANGS.includes(q)) return q;
    const v = localStorage.getItem(LANG_KEY);
    if (SUPPORTED_LANGS.includes(v)) return v;
  } catch (_) { /* ignore */ }
  return "en";
};

const HUB_ISLAND_SLUGS = ["all", "aruba", "curacao", "bonaire", "stmaarten"];
const ISLAND_TABS = ISLANDS.filter((i) => HUB_ISLAND_SLUGS.includes(i.slug));

const ObituaryCard = ({ article, timeAgo, tBadge, testIndex }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const hasImage = !!article.image_url && !imgFailed;
  const islandMeta = ISLAND_BY_SLUG[article.island];
  const to = `/article/${article.id}${article.slug ? `-${article.slug}` : ""}`;
  // Deep-link back to the specific obituary card on IslandsDaily rather
  // than the external article — matches Calvin's share directive
  // (2026-07-21) and gives us a page we control if we ever want to add
  // condolence/comment features around the shared surface.
  const shareUrl = typeof window !== "undefined"
    ? `${window.location.origin}/obituaries#obituary-${article.id}`
    : `/obituaries#obituary-${article.id}`;
  return (
    <Link
      to={to}
      onClick={() => trackPageview("/article", article.id)}
      className="group block bg-white border border-[#EEEEEE] rounded-2xl overflow-hidden hover:border-[#0AADA0] hover:shadow-md transition-all relative"
      id={`obituary-${article.id}`}
      data-testid={`obituary-card-${testIndex}`}
    >
      {hasImage ? (
        <img
          src={article.image_url}
          alt=""
          onError={() => setImgFailed(true)}
          className="w-full h-44 object-cover"
          loading="lazy"
        />
      ) : (
        <div
          className="w-full h-44"
          style={{ background: "linear-gradient(135deg,#4A5A5F 0%,#6B7C82 100%)" }}
        />
      )}
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <span
            className="inline-block text-white uppercase"
            style={{
              background: "#4A5A5F",
              fontSize: 10,
              letterSpacing: "0.05em",
              padding: "3px 8px",
              borderRadius: 999,
              fontWeight: 600,
            }}
            data-testid={`obituary-card-${testIndex}-badge`}
          >
            {tBadge}
          </span>
          {islandMeta && (
            <span className="text-[11px] text-[#6B7C82] inline-flex items-center gap-1">
              <span aria-hidden="true">{islandMeta.flag}</span>
              <span>{islandMeta.name}</span>
            </span>
          )}
          <span className="ml-auto text-[11px] text-[#9CA3AF]">
            {timeAgo(article.published_at)}
          </span>
        </div>
        <h3
          className="font-serif-display text-[18px] leading-snug text-[#1A2E35] group-hover:text-[#0AADA0] transition-colors"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
          data-testid={`obituary-card-${testIndex}-title`}
        >
          {article.title}
        </h3>
      </div>
      {/* Share affordance — top-right of the card, out of the image + title
         reading flow. Positioned absolute over the image so it never nudges
         the layout for cards with vs without images. */}
      <div className="absolute top-2 right-2 bg-white/90 backdrop-blur rounded-full shadow-sm">
        <ShareButton
          testId={`obituary-share-${testIndex}`}
          title={`${article.title} — IslandsDaily`}
          text={article.original_title || article.title}
          url={shareUrl}
        />
      </div>
    </Link>
  );
};

const ObituariesInner = () => {
  const { t, lang, timeAgo } = useT();
  // Mini-header language toggle. Uses useReactiveLang so a click here
  // writes to localStorage and dispatches `ir_lang_changed`, which the
  // outer ObituariesPage listens for and pipes back through LangContext
  // to re-render this inner tree with the new lang.
  const [__toggleLang, __setToggleLang] = useReactiveLang();
  const [island, setIsland] = useState("all");
  const [items, setItems] = useState(null); // null = loading
  const [coverage, setCoverage] = useState(null); // { aruba: n, ... }

  // Document title reacts to language changes — kept separate from the
  // one-time data-fetch effect below. Safe to depend on `t` because the
  // effect body has NO state setters (see scripts/audit_react_deps.py).
  useEffect(() => {
    document.title = `${t("obituaries_title")} — IslandsDaily`;
  }, [t]); // audit:allow-usetdep

  // Data fetch runs exactly ONCE per mount. This effect intentionally
  // has an empty dep array — an earlier `[t]` dep caused an infinite
  // re-render loop (useT() returns a fresh `t` closure every render, so
  // `[t]` fires on every render → refetches coverage → setCoverage → new
  // render → refires the effect → scrollTo(0) → scroll-lock). Root cause
  // was pre-existing (introduced in the file's first commit) but became
  // visibly severe once the API responded fast enough for the loop to
  // clip user scroll input. See handoff 2026-07-21 for RCA.
  //
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    api
      .get("/obituaries/coverage")
      .then((r) => {
        const map = {};
        (r?.data?.by_island || []).forEach((i) => { map[i.slug] = i.count; });
        setCoverage(map);
      })
      .catch(() => setCoverage({}));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    const params = { limit: 60, lang };
    if (island && island !== "all") params.island = island;
    api
      .get("/articles/obituaries", { params })
      .then((r) => { if (!cancelled) setItems(Array.isArray(r.data) ? r.data : []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [island, lang]);

  const emptyKey =
    island === "all" ? "obituaries_empty_all" : "obituaries_empty_island";

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="obituaries-page">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[#EEEEEE]">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            to="/"
            className="font-serif-display text-[18px] leading-none"
            data-testid="obituaries-home-link"
          >
            <span className="text-[#1A1A1A]">Islands</span>
            <span className="text-[#0AADA0]">Daily</span>
          </Link>
          <span className="text-[#D0D5DD]">·</span>
          <span className="text-[12px] text-[#6B7280] font-medium">
            {t("obituaries_title")}
          </span>
          {/* __LANG_TOGGLE__ */}
          <LanguageToggle lang={__toggleLang} onChange={__setToggleLang} />
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14">
        <h1
          className="font-serif-display text-[32px] sm:text-[42px] leading-tight text-[#1A2E35]"
          data-testid="obituaries-heading"
        >
          {t("obituaries_title")}
        </h1>
        <p className="text-[14px] sm:text-[15px] text-[#6B7280] mt-3 max-w-2xl">
          {t("obituaries_subtitle")}
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 mt-8">
        <div
          className="flex flex-wrap items-center gap-2"
          role="tablist"
          data-testid="obituaries-island-filter"
        >
          {ISLAND_TABS.map((i) => {
            const active = island === i.slug;
            return (
              <button
                key={i.slug}
                role="tab"
                aria-selected={active}
                onClick={() => setIsland(i.slug)}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full text-[13px] transition-colors border ${
                  active
                    ? "bg-[#0AADA0] text-white border-[#0AADA0]"
                    : "bg-white text-[#1A2E35] border-[#EEEEEE] hover:border-[#0AADA0]"
                }`}
                data-testid={`obituaries-island-${i.slug}`}
              >
                <span aria-hidden="true">{i.flag}</span>
                <span>{i.name}</span>
                {coverage && typeof coverage[i.slug] === "number" && i.slug !== "all" && (
                  <span className={`text-[10px] ${active ? "text-white/80" : "text-[#9CA3AF]"}`}>
                    {coverage[i.slug]}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
        {items === null && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
            data-testid="obituaries-loading"
          >
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-72 rounded-2xl bg-[#F0F0F0] animate-pulse"
              />
            ))}
          </div>
        )}
        {items !== null && items.length === 0 && (
          <div
            className="max-w-xl mx-auto text-center py-14 px-6 rounded-2xl border border-[#EEEEEE] bg-white"
            data-testid="obituaries-empty"
          >
            <div className="text-[36px] mb-3" aria-hidden="true">🕊️</div>
            <p className="text-[14px] text-[#6B7C82] leading-relaxed">
              {t(emptyKey)}
            </p>
          </div>
        )}
        {items !== null && items.length > 0 && (
          <div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5"
            data-testid="obituaries-grid"
          >
            {items.map((article, idx) => (
              <ObituaryCard
                key={article.id || idx}
                article={article}
                timeAgo={timeAgo}
                tBadge={t("obituaries_badge")}
                testIndex={idx}
              />
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default function ObituariesPage() {
  const [lang, setLang] = useState(readLang);
  useEffect(() => {
    const refresh = () => setLang(readLang());
    const onStorage = (e) => { if (e.key === LANG_KEY) refresh(); };
    window.addEventListener("ir_lang_changed", refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("ir_lang_changed", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, []);
  return (
    <LangContext.Provider value={lang}>
      <ObituariesInner />
    </LangContext.Provider>
  );
}
